import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { PrintableThread } from "./PrintableThread";
import { Popover } from "./ui/Popover";
import { cn } from "./ui/cn";
import type { TicketDetail } from "../types";

/**
 * Print, or save, the whole conversation.
 *
 * Sits with the pane toggles because it is the same kind of control: it acts on
 * the thread as a whole rather than on anything in it, and none of them belong
 * to the conversation itself.
 *
 * ## Why a menu rather than a button
 *
 * One question has to be asked, and it is not a preference. A printed thread is
 * usually printed to leave the building -- a customer, a supplier, a case file
 * -- and an internal note is the part of a ticket written on the understanding
 * that those people will not read it. So notes are excluded by default, the
 * choice is made before the dialog opens rather than discovered afterwards in
 * the preview, and the paper says when something was left out.
 *
 * Saving is not a second item. Every print dialog offers "Save as PDF" as a
 * destination, which produces a better file than we could -- real text, the
 * reader's paper size, no dependency -- so "Download" would be a worse copy of
 * a thing that is already there.
 */
export function PrintThreadButton({ detail }: { detail: TicketDetail }) {
  /**
   * Null when nothing is being printed.
   *
   * The printable rendering is mounted on demand rather than kept hidden in
   * the page: it is a second copy of an entire thread, images and quoted
   * history included, and a ticket nobody is printing should not be paying for
   * it. Holding the notes decision here is what makes it one piece of state --
   * "we are printing, and this is how".
   */
  const [printing, setPrinting] = useState<{ includeNotes: boolean } | null>(
    null,
  );

  const hasNotes = detail.messages.some((message) => message.kind === "note");

  /*
   * Print once the subtree is on the page, and take it down when the dialog
   * closes. `afterprint` fires on cancel as well as on print, which is what
   * makes it the right signal -- an agent who thought better of it should not
   * be left with a second thread mounted for the rest of the session.
   *
   * The deferral is not decorative: `window.print()` blocks, and calling it in
   * the same tick as the mount can capture the page before the browser has laid
   * the new subtree out. The symptom is a blank first page.
   *
   * A timeout rather than `requestAnimationFrame`, which is the obvious choice
   * and the wrong one -- a browser does not animate a tab nobody is looking at,
   * so a frame callback in a background tab never runs. That leaves the print
   * pending and a second copy of the thread mounted with nothing to take it
   * down. A timeout is throttled there, not cancelled.
   */
  useEffect(() => {
    if (!printing) return;

    const done = () => setPrinting(null);
    window.addEventListener("afterprint", done);
    const timer = window.setTimeout(() => window.print(), 0);

    return () => {
      window.removeEventListener("afterprint", done);
      window.clearTimeout(timer);
    };
  }, [printing]);

  const [open, setOpen] = useState(false);

  const start = (includeNotes: boolean) => {
    setOpen(false);
    setPrinting({ includeNotes });
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        label="Print conversation"
        /* Downwards and right-aligned: this lives at the right-hand end of a
           header row, which is the one place the composer's default of "up and
           left" is wrong in both axes. */
        side="bottom"
        align="right"
        className="w-60 p-1"
        trigger={
          <span
            aria-hidden
            title="Print conversation"
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-md border border-line",
              "text-xs text-content hover:bg-surface-2",
            )}
          >
            ⎙
          </span>
        }
      >
        <MenuItem onClick={() => start(false)}>
          Print conversation
          {hasNotes ? (
            <span className="block text-[11px] text-muted">
              Without internal notes
            </span>
          ) : null}
        </MenuItem>

        {/* Offered only when there is something to include, so the menu does
            not raise the question on the many tickets that have no notes. */}
        {hasNotes ? (
          <MenuItem onClick={() => start(true)}>
            Print with internal notes
            <span className="block text-[11px] text-muted">
              Not for the customer
            </span>
          </MenuItem>
        ) : null}

        <p className="mt-1 border-t border-line px-2 pb-1 pt-1.5 text-[11px] text-muted">
          To save a copy, choose “Save as PDF” as the printer.
        </p>
      </Popover>

      {/*
        Portalled to the body, outside the application root, because the print
        stylesheet takes that root off the page wholesale -- see index.css. A
        printable subtree rendered inside the thing being hidden is a printable
        subtree that does not print.
      */}
      {printing
        ? createPortal(
            <PrintableThread
              detail={detail}
              includeNotes={printing.includeNotes}
            />,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded px-2 py-1.5 text-left text-xs text-content hover:bg-surface-2"
    >
      {children}
    </button>
  );
}
